import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { EloDisplay, LeagueBadge, BadgeList } from '../../components/gamification';
import { Trophy, Medal, Crown, TrendingUp, TrendingDown, Minus, Users, Building2, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.05 } }
};

export default function LeaderboardPage() {
    const { user } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [myRank, setMyRank] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('global'); // global, agency
    const [leagueFilter, setLeagueFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        loadData();
    }, [filter, leagueFilter, page]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load my rank if agent
            if (user?.role === 'agent') {
                const rankRes = await api.get('/leaderboard/my-rank');
                setMyRank(rankRes.data);
            }

            // Load leaderboard
            let endpoint = '/leaderboard/global';
            let params = { page, limit: 20 };
            
            if (filter === 'agency' && user?.agency_id) {
                endpoint = `/leaderboard/agency/${user.agency_id}`;
                params = {};
            } else if (leagueFilter !== 'all') {
                params.league = leagueFilter;
            }

            const res = await api.get(endpoint, { params });
            setLeaderboard(res.data.leaderboard || []);
            setTotalPages(res.data.pages || 1);
        } catch (error) {
            console.error('Error loading leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRankIcon = (rank) => {
        if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
        if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
        if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
        return <span className="w-6 h-6 flex items-center justify-center font-bold text-muted-foreground">{rank}</span>;
    };

    const getTrendIcon = (trend) => {
        if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
        if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
        return <Minus className="w-4 h-4 text-muted-foreground" />;
    };

    return (
        <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn} className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Trophy className="w-7 h-7 text-[#FF914D]" />
                        <h1 className="text-3xl font-black text-foreground">HomlyScore</h1>
                    </div>
                    <p className="text-muted-foreground">Classement national des agents immobiliers</p>
                </div>
            </motion.div>

            {/* My Rank Card (for agents) */}
            {user?.role === 'agent' && myRank?.stats && (
                <motion.div variants={fadeIn}>
                    <div className="bg-gradient-to-r from-[#681793]/20 to-[#FF914D]/20 rounded-2xl p-6 border border-[#681793]/30">
                        <div className="flex flex-wrap items-center justify-between gap-6">
                            <div className="flex items-center gap-6">
                                <div className="text-center">
                                    <p className="text-5xl font-black bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                        #{myRank.global_rank}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Classement global</p>
                                </div>
                                <div className="h-16 w-px bg-white/10" />
                                <EloDisplay 
                                    elo={myRank.stats.elo}
                                    league={myRank.stats.league}
                                    leagueInfo={myRank.stats.league_info}
                                    trend={myRank.stats.trend}
                                    size="lg"
                                    showProgress
                                />
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-white/50">Dans votre agence</p>
                                <p className="text-3xl font-bold text-white">
                                    #{myRank.agency_rank} 
                                    <span className="text-base font-normal text-white/40"> / {myRank.agency_total}</span>
                                </p>
                            </div>
                        </div>
                        {myRank.stats.badges?.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-white/10">
                                <BadgeList badges={myRank.stats.badges} size="sm" />
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Filters */}
            <motion.div variants={fadeIn} className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1.5 border border-white/10">
                    <Button
                        variant={filter === 'global' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => { setFilter('global'); setPage(1); }}
                        className={cn(
                            "rounded-lg",
                            filter === 'global' 
                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                    >
                        <Users className="w-4 h-4 mr-2" />
                        National
                    </Button>
                    {user?.agency_id && (
                        <Button
                            variant={filter === 'agency' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => { setFilter('agency'); setPage(1); }}
                            className={cn(
                                "rounded-lg",
                                filter === 'agency' 
                                    ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white' 
                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                            )}
                        >
                            <Building2 className="w-4 h-4 mr-2" />
                            Mon agence
                        </Button>
                    )}
                </div>

                {filter === 'global' && (
                    <Select value={leagueFilter} onValueChange={(v) => { setLeagueFilter(v); setPage(1); }}>
                        <SelectTrigger className="w-40 bg-card border-border text-foreground">
                            <SelectValue placeholder="Ligue" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10">
                            <SelectItem value="all">Toutes les ligues</SelectItem>
                            <SelectItem value="diamond">Diamant</SelectItem>
                            <SelectItem value="platinum">Platine</SelectItem>
                            <SelectItem value="gold">Or</SelectItem>
                            <SelectItem value="silver">Argent</SelectItem>
                            <SelectItem value="bronze">Bronze</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </motion.div>

            {/* Leaderboard Table */}
            <motion.div variants={fadeIn}>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
                    <div className="p-5 border-b border-white/10">
                        <h2 className="text-lg font-bold text-foreground">
                            {filter === 'global' ? 'Classement national' : 'Classement agence'}
                        </h2>
                    </div>
                    <div className="p-5">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : leaderboard.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                                    <Trophy className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <p className="text-muted-foreground">Aucun agent dans le classement pour le moment</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {leaderboard.map((entry, idx) => (
                                    <motion.div 
                                        key={entry.agent_id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.03 }}
                                        className={cn(
                                            'flex items-center gap-4 p-4 rounded-xl transition-all duration-300',
                                            entry.rank <= 3 
                                                ? 'bg-gradient-to-r from-[#681793]/10 to-transparent border border-[#681793]/20' 
                                                : 'bg-white/5 hover:bg-white/10 border border-transparent',
                                            entry.agent_id === user?.id && 'ring-2 ring-[#681793] bg-[#681793]/10'
                                        )}
                                    >
                                        {/* Rank */}
                                        <div className="w-10 flex justify-center">
                                            {getRankIcon(entry.rank)}
                                        </div>

                                        {/* Avatar & Name */}
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold">
                                                {entry.agent_name?.charAt(0) || '?'}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-foreground truncate flex items-center gap-2">
                                                    {entry.agent_name}
                                                    {entry.agent_id === user?.id && (
                                                        <span className="text-xs bg-gradient-to-r from-[#681793] to-[#FF914D] text-white px-2 py-0.5 rounded-full">Vous</span>
                                                    )}
                                                </p>
                                                {entry.agency_name && (
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {entry.agency_name} {entry.agency_city && `• ${entry.agency_city}`}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* League */}
                                        <div className="hidden sm:flex items-center gap-2">
                                            <LeagueBadge league={entry.league} showName />
                                        </div>

                                        {/* Stats */}
                                        <div className="text-right">
                                            <div className="flex items-center gap-1 justify-end">
                                                <span className="font-bold text-lg text-foreground">{Math.round(entry.elo)}</span>
                                                {getTrendIcon(entry.trend)}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {entry.total_ratings} avis • {entry.avg_rating?.toFixed(1)}/5
                                            </p>
                                        </div>

                                        {/* Badges count */}
                                        <div className="hidden md:block text-center min-w-16">
                                            {entry.badges_count > 0 ? (
                                                <div className="flex items-center gap-1 justify-center">
                                                    <Trophy className="w-4 h-4 text-[#FF914D]" />
                                                    <span className="text-sm font-medium text-foreground">{entry.badges_count}</span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-muted-foreground">—</span>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-white/10">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}
                                    className="border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                    Précédent
                                </Button>
                                <span className="text-sm text-muted-foreground">
                                    Page {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page === totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                    className="border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                    Suivant
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
